// Rate Limits tab — extracted from the legacy SecurityDashboard (wave 3:
// the modern table, Addis 12-hour times).
//
// One-time fetch of /rate_limits so the cost only hits when the admin
// opens this tab. Provides per-entry block toggle + delete; both writes
// are unchanged (update(rate_limits/<type>/<key>, { blocked }) and
// update(rate_limits/<type>/<key>, null)).

import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ref, get, update } from 'firebase/database';
import { FirebaseContext } from 'common';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { showToast, confirmDialog, InlineMessage, EmptyState } from '../../components/feedback';
import { DataTable, StatusChip, formatNumber } from '../../components/modern';
import { tokens as c } from '../../theme';

export default function RateLimits() {
  const { t } = useTranslation();
  const { database } = useContext(FirebaseContext);
  const auth = useSelector((state) => state.auth);
  const isAdmin = auth?.profile?.usertype === 'admin';

  const [rateLimits, setRateLimits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!database || !isAdmin) return;
    let cancelled = false;
    const fetchRateLimits = async () => {
      const snapshot = await get(ref(database, 'rate_limits'));
      const data = snapshot.val();
      if (cancelled) return;
      if (data) {
        const limitsArray = [];
        Object.keys(data).forEach((type) => {
          if (data[type] && typeof data[type] === 'object') {
            const typeData = data[type];
            Object.keys(typeData).forEach((key) => {
              const limitData = typeData[key];
              if (limitData && typeof limitData === 'object') {
                limitsArray.push({
                  id: `${type}-${key}`,
                  type,
                  key,
                  requests: limitData.requests || [],
                  count: (limitData.requests || []).length,
                  lastRequest: limitData.lastRequest,
                  blocked: limitData.blocked || false,
                });
              }
            });
          }
        });
        setRateLimits(limitsArray);
      } else {
        setRateLimits([]);
      }
      setLoading(false);
    };
    fetchRateLimits();
    return () => {
      cancelled = true;
    };
  }, [database, isAdmin]);

  const handleDeleteRateLimit = async (type, key) => {
    if (!database) return;
    const ok = await confirmDialog({
      title: t('rate_limit_delete_confirm', { key, defaultValue: 'Delete the rate limit for {{key}}?' }),
      confirmLabel: t('delete'),
      // Not the pack's 'cancel': in Amharic and Tigrinya it is the same word
      // as 'delete' (ሰርዝ), which would label both buttons alike.
      cancelLabel: t('confirm_cancel', { defaultValue: 'Cancel' }),
      destructive: true,
    });
    if (!ok) return;
    try {
      const rateLimitRef = ref(database, `rate_limits/${type}/${key}`);
      await update(rateLimitRef, null);
      setRateLimits((prev) => prev.filter((r) => r.id !== `${type}-${key}`));
    } catch (error) {
      console.error('Failed to delete rate limit:', error);
      showToast.error(t('rate_limit_delete_failed', { defaultValue: 'Could not delete the rate limit entry.' }));
    }
  };

  const handleToggleBlocked = async (type, key, currentBlocked) => {
    if (!database) return;
    try {
      const rateLimitRef = ref(database, `rate_limits/${type}/${key}`);
      await update(rateLimitRef, { blocked: !currentBlocked });
      setRateLimits((prev) => prev.map((r) => (r.id === `${type}-${key}` ? { ...r, blocked: !currentBlocked } : r)));
    } catch (error) {
      console.error('Failed to update blocked status:', error);
      showToast.error(t('rate_limit_block_failed', { defaultValue: 'Could not change the blocked status.' }));
    }
  };

  if (!isAdmin) {
    return (
      <Paper sx={{ p: 2 }}>
        <EmptyState icon={<LockOutlinedIcon />} title={t('admin_access_required', { defaultValue: 'Admin access required.' })} />
      </Paper>
    );
  }

  const columns = [
    { title: t('type', { defaultValue: 'Type' }), field: 'type' },
    {
      title: t('rate_limits_identifier', { defaultValue: 'Identifier' }),
      field: 'key',
      render: (limit) => (
        <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', wordBreak: 'break-all' }}>
          {limit.key}
        </Box>
      ),
    },
    {
      title: t('rate_limits_count', { defaultValue: 'Request Count' }),
      field: 'count',
      type: 'number',
      render: (limit) =>
        limit.count >= 5 ? <StatusChip tone="error" label={formatNumber(limit.count || 0)} /> : formatNumber(limit.count || 0),
    },
    { title: t('rate_limits_last_request', { defaultValue: 'Last Request' }), field: 'lastRequest', type: 'datetime' },
    {
      title: t('rate_limits_blocked', { defaultValue: 'Blocked' }),
      field: 'blocked',
      type: 'boolean',
      render: (limit) => (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <Switch
            checked={limit.blocked || false}
            onChange={() => handleToggleBlocked(limit.type, limit.key, limit.blocked)}
            size="small"
            color="error"
            inputProps={{ 'aria-label': `${t('rate_limits_blocked', { defaultValue: 'Blocked' })}: ${limit.key}` }}
          />
          <Box component="span" sx={{ color: limit.blocked ? c.DANGER_TEXT : c.INK_2, fontWeight: limit.blocked ? 600 : 400 }}>
            {limit.blocked ? t('yes', { defaultValue: 'Yes' }) : t('no', { defaultValue: 'No' })}
          </Box>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <InlineMessage kind="info">
        {t('rate_limits_info', { defaultValue: 'Rate limits are automatically cleaned up every hour. Active limits shown below.' })}
      </InlineMessage>
      <DataTable
        title={t('security_tab_rate_limits', { defaultValue: 'Rate Limits' })}
        rows={rateLimits}
        columns={columns}
        rowKey="id"
        loading={loading}
        countText={({ shown, total, query, loading: busy }) =>
          busy
            ? t('loading', { defaultValue: 'Loading…' })
            : query
              ? t('table_count_shown', { shown, total, defaultValue: '{{shown}} of {{total}}' })
              : t('rate_limits_rows', {
                  count: total,
                  defaultValue_one: '{{count}} active limit',
                  defaultValue_other: '{{count}} active limits',
                  defaultValue: '{{count}} active limits',
                })
        }
        searchPlaceholder={t('rate_limits_search', { defaultValue: 'Search type or identifier' })}
        rowLabel={(limit) => limit.key}
        rowSx={(limit) => (limit.blocked ? { '& > td': { bgcolor: `${c.DANGER_TINT} !important` } } : null)}
        actions={[
          {
            key: 'delete',
            label: t('rate_limits_delete', { defaultValue: 'Delete rate limit entry' }),
            icon: <DeleteOutlineRoundedIcon />,
            inline: true,
            destructive: true,
            onClick: (limit) => handleDeleteRateLimit(limit.type, limit.key),
          },
        ]}
        prefsKey="security_rate_limits"
        minWidth={760}
        emptyState={({ query }) => (query ? null : { icon: <SpeedOutlinedIcon />, title: t('rate_limits_empty', { defaultValue: 'No active rate limits' }) })}
      />
    </Box>
  );
}
